import { useEffect, useState } from 'react';

import { useMqtt } from '@/contexts/mqtt-provider-context';

import { decodeMqttText } from './payload';

export interface LiveBioscopeData {
    /**
     * Latest depth in metres, parsed from the `Depth` field of
     * `bioscope/data/response/{id}` (e.g. `"8.6m"` → `8.6`).
     * Null until the first auto-push arrives.
     */
    depth: number | null;
    /**
     * LED luminosity setpoint (0–100), parsed from the `Luminosity` field
     * (e.g. `"100.0%"` → `100`). Null until the first auto-push arrives.
     */
    lux: number | null;
    /**
     * Pitch angle in degrees, parsed from the `Pitch` field of
     * `bioscope/data/response/{id}` (e.g. `"1.2°"` → `1.2`).
     * Null until the first auto-push arrives.
     */
    pitch: number | null;
    /**
     * Roll angle in degrees, parsed from the `Roll` field of
     * `bioscope/data/response/{id}` (e.g. `"0.8°"` → `0.8`).
     * Null until the first auto-push arrives.
     */
    roll: number | null;
    /**
     * Online state from the retained `v1/bioscope/{id}/online` topic.
     * - `true` / `false` once a retained byte has been received.
     * - `null` while we have no signal yet. Callers should fall back to a
     *   server-derived state in that case.
     */
    online: boolean | null;
    /**
     * Firmware timestamp from the payload's `timestamp` field. `null` when
     * the field is absent or unparseable so callers can suppress freshness UI.
     * Using the firmware timestamp means retained messages correctly show when
     * the bioscope sent the data, not when the browser received it.
     */
    lastMessageAt: Date | null;
}

const INITIAL: LiveBioscopeData = {
    depth: null,
    lux: null,
    pitch: null,
    roll: null,
    online: null,
    lastMessageAt: null,
};

// Strip a single trailing unit suffix (°, m, %) and parse the leading number.
// Returns null when the value isn't a finite number.
const parseNumeric = (raw: unknown): number | null => {
    if (raw == null) return null;
    const n = Number.parseFloat(String(raw));
    return Number.isFinite(n) ? n : null;
};

/**
 * Passively subscribes to two MQTT topics:
 *
 *  - `v1/bioscope/{id}/online` — retained single byte (0x01 / ASCII '1' = online).
 *  - `bioscope/data/response/{id}` — auto-pushed by the bioscope every ~minute;
 *    confirmed to carry `Depth` ("8.6m"), `Luminosity` ("100.0%"), `Roll`,
 *    `Pitch`, `Water Temperature`, and a `timestamp` field.
 *
 * `depth`, `lux`, `pitch` and `roll` all come from the same auto-push, so they
 * always share the same `lastMessageAt` timestamp and match what
 * BioscopeSystemReadings shows.
 *
 * No `fetch_bioscope_data` request is sent — the bioscope drives the cadence.
 */
export const useLiveBioscopeData = (bioscopeId: number | null | undefined): LiveBioscopeData => {
    const { client, isConnected, subscribe } = useMqtt();
    const [state, setState] = useState<LiveBioscopeData>(INITIAL);

    useEffect(() => {
        // Reset whenever bioscopeId changes so the new pen doesn't briefly
        // show the previous pen's values.
        setState(INITIAL);

        if (!isConnected || !client || !bioscopeId) return;

        const onlineTopic = `v1/bioscope/${bioscopeId}/online`;
        const dataTopic = `bioscope/data/response/${bioscopeId}`;

        const handler = (topic: string, message: Uint8Array) => {
            if (topic === onlineTopic) {
                // An empty payload (length 0) is the broker mechanism for clearing a
                // retained topic — not a genuine offline signal. Ignore it so the card
                // falls back to the server-derived status instead of showing Offline.
                if (message.length === 0) return;
                const byte = message[0];
                setState((prev) => ({ ...prev, online: byte === 1 || byte === 0x31 }));
                return;
            }
            if (topic === dataTopic) {
                try {
                    const payload = JSON.parse(decodeMqttText(message));

                    // All numeric fields use parseNumeric which strips any trailing
                    // unit suffix (m, %, °) and returns null for non-finite values.
                    const depth = parseNumeric(payload?.Depth ?? payload?.depth);

                    // Luminosity needs the '%' stripped before parsing.
                    const luxRaw = payload?.Luminosity ?? payload?.luminosity;
                    const lux =
                        luxRaw != null
                            ? parseNumeric(String(luxRaw).replace('%', '').trim())
                            : null;

                    // Pitch/Roll: firmware sends strings like "1.2°" or bare numbers.
                    const pitch = parseNumeric(payload?.Pitch ?? payload?.pitch);
                    const roll = parseNumeric(payload?.Roll ?? payload?.roll);

                    // Use the firmware timestamp from the payload so retained messages
                    // show when the bioscope sent the data, not when the browser received it.
                    // Null when the field is absent or unparseable — callers suppress the tooltip.
                    const tsRaw = payload?.timestamp;
                    const lastMessageAt =
                        tsRaw != null && !Number.isNaN(Date.parse(tsRaw)) ? new Date(tsRaw) : null;

                    setState((prev) => ({ ...prev, depth, lux, pitch, roll, lastMessageAt }));
                } catch {
                    // payload not JSON; ignore silently
                }
            }
        };

        subscribe(onlineTopic, { qos: 1 });
        subscribe(dataTopic, { qos: 1 });
        client.on('message', handler);

        return () => {
            client.off('message', handler);
            // Not unsubscribing on unmount intentionally: other pen cards may
            // share the same topic. Each handler filters by exact topic string.
        };
    }, [bioscopeId, isConnected, client, subscribe]);

    return state;
};
